import { type FlowTask, flowTasksService } from "@/app/providers/services";
import {
  AssigneeSelector,
  DateSelector,
  PrioritySelector,
  StatusSelector,
  TaskDetail,
  TextInput,
  TitleField,
} from "@/features/tasks/FormControls";
import { createFlowTask, type FlowFormTask } from "@/features/tasks/taskUtils";
import { type DialogAction, DialogActions } from "@/ui/dialogs/actions";
import { WORKSPACE_LAYER } from "@/ui/dialogs/layers";
import Form, { type FormReturn, useFormContext } from "@imminently/decisively-ui/Form";
import Icon from "@imminently/decisively-ui/Icon";
import { ProjectWriterGuard } from "@/auth/ComponentGuard";
import { useProject, useRelease, useWorkspace } from "@/common/hooks";
import { useNotification } from "@/common/notifications";
import Dialog, { useDialog, useDialogContext } from "@imminently/decisively-ui/Dialog";
import { DialogControls } from "@/ui/dialogs/DialogHeader";
import { useTranslation } from "react-i18next";
import { useBasicInterview } from "@/hooks/use-interview";
import { Interview, useInterview } from "@imminently/interview-ui";
import { cn } from "@/lib/utils";
import Breadcrumb from "@imminently/decisively-ui/Breadcrumb";
import { useScope } from "@/redux/scope";
import { crudGetOne } from "@/legacy/redux";
import { useEffect } from "react";
import { useDispatch } from "react-redux";

const InterviewContent = ({ children }: { children?: React.ReactNode }) => {
  const { state, session } = useInterview();
  if (state !== "success" || !session) {
    return null; // Don't render if not in success state
  }
  return (
    <div
      className={cn("relative flex flex-1 flex-col min-h-0")}
      data-slot="content"
      slot-content=""
    >
      <Interview.Processing className="absolute top-0 left-0 right-0 rounded-none h-1" />
      <Interview.Title className="p-4 w-full max-w-3xl mx-auto" />
      <Interview.Form className="flex-1 overflow p-4 w-full max-w-3xl mx-auto" />
      <Interview.Validations />
      <div className="flex gap-2 items-center justify-between p-4 w-full max-w-3xl mx-auto">
        <Interview.Back variant="ghost" />
        <Interview.Next />
      </div>
      {children}
    </div>
  );
};

const TaskFooter = ({
  task,
  close,
  standAlone,
  taskForm,
}: {
  task: FlowTask;
  close: () => void;
  standAlone?: boolean;
  taskForm: FormReturn<FlowFormTask>;
}) => {
  const notify = useNotification();
  const { t } = useTranslation();
  const methods = useFormContext();
  const { manager } = useInterview();
  const { action, query } = flowTasksService.useMutateResource(task, {});
  const { handleSubmit } = methods;
  const lastStep = manager.isLastStep;

  const taskUpdatePayload = () => {
    const data = taskForm.getValues();

    return {
      title: data.title,
      status: data.status,
      priority: data.priority,
      dueDate: data.dueDate || undefined,
      assignees: data.assigneesData?.map((assignee) => assignee.identity_id) ?? [],
    };
  };

  const saveChanges = () => {
    action(taskUpdatePayload())
      .then(() => {
        flowTasksService.invalidateAllQueries();
        notify.info("Task updated");
      })
      .catch((error) => {
        notify.error("Failed to update task");
        console.error(error);
      });
  };

  const approve = handleSubmit(({ justification, ...interviewData }) => {
    const outcome = task.approvalOutcome ?? true;
    const payload = {
      ...taskUpdatePayload(),
      outcome,
      justification,
      data: interviewData,
    };
    action(payload)
      .then((res) => {
        flowTasksService.invalidateAllQueries();
        close();
      })
      .catch((e) => {
        notify.error(t("tasks.error_approve_msg"));
        console.error(e);
      });
  });

  const reject = handleSubmit(({ justification, ...interviewData }) => {
    const outcome = task.rejectionOutcome ?? true;
    const payload = {
      ...taskUpdatePayload(),
      outcome,
      justification,
      data: interviewData,
    };
    action(payload)
      .then((res) => {
        flowTasksService.invalidateAllQueries();
        close();
      })
      .catch((e) => {
        notify.error(t("tasks.error_reject_msg"));
        console.error(e);
      });
  });

  const primaryAction: DialogAction = {
    name: task.approvalLabel ?? t("tasks.approve_label"),
    onClick: approve,
    loading: query.isLoading,
    disabled: query.isLoading || !lastStep,
  };

  const secondaryAction: DialogAction[] = [
    {
      name: "Save changes",
      onClick: saveChanges,
      loading: query.isLoading,
      disabled: query.isLoading,
      variant: "outline",
    },
    {
      name: task.rejectionLabel ?? t("tasks.reject_label"),
      onClick: reject,
      loading: query.isLoading,
      disabled: query.isLoading || !lastStep,
      intent: "destructive",
      variant: "outline"
    },
  ];

  return (
    <div className={cn("flex flex-col gap-4 p-4 bg-card border-t rounded-b-lg", standAlone && "absolute w-full bottom-0" )}>
      <TextInput
        label={t("explanation.justification")}
        name="justification"
        placeholder={t("tasks.justification_placeholder")}
        multiline
        minRows={4}
      />
      <ProjectWriterGuard>
        <DialogActions className="p-0" primary={primaryAction} secondary={secondaryAction} />
      </ProjectWriterGuard>
    </div>
  );
};

export const FlowTaskForm = ({
  task,
  close,
  standAlone,
}: { task: FlowTask; close: () => void; standAlone?: boolean }) => {
  const { t } = useTranslation();

  const options = useBasicInterview((manager) => {
    manager.load({
      project: task.model,
      sessionId: task.session,
      interactionId: task.interaction,
    });
  });

  return (
    <Form<FlowFormTask> defaultValues={createFlowTask(task)} asChild>
      {(taskForm) => (
        <div id="task-form" className="flex flex-col flex-1 min-h-0">
          <div className="@container flex flex-col gap-4 p-4 border-b">
            <TitleField />
            <div className="flex flex-col gap-4 @lg:grid @lg:grid-cols-2">
              <TaskDetail
                label={t("metadata.status")}
                icon={<Icon name="record" />}
              >
                <StatusSelector omit={["closed", "rejected", "approved"]} />
              </TaskDetail>
              <TaskDetail
                label={t("tasks.assignee")}
                icon={<Icon name="users" />}
              >
                <AssigneeSelector />
              </TaskDetail>
              <TaskDetail
                label={t("tasks.due_date")}
                icon={<Icon name="date-range" />}
              >
                <DateSelector label="Pick a date" />
              </TaskDetail>
              <TaskDetail
                label={t("tasks.priority_label")}
                icon={<Icon name="flag" />}
              >
                <PrioritySelector />
              </TaskDetail>
            </div>
          </div>
          <Interview options={options}>
            <Interview.Loading className="flex flex-1 items-center justify-center" />
            <Interview.Error className="flex flex-1 items-center justify-center" />
            <InterviewContent>
              <TaskFooter
                task={task}
                close={close}
                standAlone={standAlone}
                taskForm={taskForm}
              />
            </InterviewContent>
          </Interview>
        </div>
      )}
    </Form>
  );
};

const TaskModal = ({ task, close, standAlone }: { task: FlowTask; close: () => void; standAlone: boolean }) => {
  const dispatch = useDispatch();
  const { workspace: workspaceId, project: projectId, release: buildRelease } = useScope();
  const workspace = useWorkspace(workspaceId);
  const project = useProject(projectId);
  const releaseId = (task as any).release ?? buildRelease;
  const release = useRelease(releaseId);
  const dialogContext = useDialogContext();
  const fullScreen = dialogContext?.fullScreen ?? false;
  const headerHeight = 40 + 62 + 58; // combined size in px of the header, info, status bar and tabs
  // of not fullscreen, include the 2rem margin around the modal
  const height = fullScreen ? `calc(100vh - ${headerHeight}px)` : `calc(100vh - 4rem - ${headerHeight}px)`;

  useEffect(() => {
    if (releaseId) {
      dispatch(crudGetOne("releases", releaseId, { refresh: false, onFailure: {} }));
    }
  }, [dispatch, releaseId]);

  return (
    <Dialog.Content>
      <Dialog.Header className="flex flex-row items-center justify-between border-b p-2">
        {/* TODO this should actually be driven off the task if edit, scope if create */}
        <Dialog.Title>
          <Breadcrumb>
            <Breadcrumb.List className="pl-2">
              <Breadcrumb.Item>
                <Breadcrumb.Page>{workspace?.name}</Breadcrumb.Page>
              </Breadcrumb.Item>
              <Breadcrumb.Separator />
              <Breadcrumb.Item>
                <Breadcrumb.Page>{project?.name}</Breadcrumb.Page>
              </Breadcrumb.Item>
              <Breadcrumb.Separator />
              <Breadcrumb.Item>
                <Breadcrumb.Page>{release ? `Release ${release.releaseNo}` : "Any release"}</Breadcrumb.Page>
              </Breadcrumb.Item>
            </Breadcrumb.List>
          </Breadcrumb>
        </Dialog.Title>
        
        <DialogControls className="relative p-0" allowResize />
      </Dialog.Header>
      <Dialog.Body className={"scrollable flex-1 p-0 min-w-[calc(100vw - 4rem)]"} style={{ height }}>
        <FlowTaskForm
          task={task}
          close={close}
          standAlone={standAlone}
        />
      </Dialog.Body>
    </Dialog.Content>
  );
};

export const useSessionTaskModal = () => {
  const { pushDialog } = useDialog();

  return (task: FlowTask) => {
    pushDialog({
      layer: WORKSPACE_LAYER,
      options: {
        intent: "cover",
        defaultFullScreen: false,
      },
      component: ({ close }) => (
        <TaskModal
          task={task}
          close={close}
          standAlone={false}
        />
      ),
    });
  };
};
